/**
 * Shared control bindings used by both tools' composition modules:
 * slider/switch persistence bindings, the click-to-edit bpm readout, and
 * the hint toggletips. Extracted from Clicker's original Main class;
 * behaviour unchanged.
 */

/**
 * Wire up the per-control hint toggletips within a subtree: click the badge
 * to toggle its popover, click anywhere else (or press Escape) to dismiss.
 * Only one popover is open at a time.
 */
function wireHintPopovers(root: ParentNode = document) {
    const closers: (() => void)[] = [];

    root.querySelectorAll<HTMLElement>('.hint').forEach((hint) => {
        const toggle = hint.querySelector<HTMLButtonElement>('.hint-toggle');
        const popover = hint.querySelector<HTMLElement>('.hint-popover');
        if (!toggle || !popover) {
            return;
        }

        const close = () => {
            popover.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
        };
        closers.push(close);

        toggle.addEventListener('click', (event) => {
            event.stopPropagation();
            if (popover.hidden) {
                closers.forEach(c => c());
                popover.hidden = false;
                toggle.setAttribute('aria-expanded', 'true');
            } else {
                close();
            }
        });

        popover.addEventListener('click', (event) => event.stopPropagation());

        document.addEventListener('click', close);
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !popover.hidden) {
                close();
                toggle.focus();
            }
        });
    });
}

/**
 * Touch/pen track-press guard for native range inputs.
 *
 * Blink moves a range input's thumb to wherever a press lands, the moment
 * it lands (crbug.com/40843079), so a page scroll on touch (or a misplaced
 * click on mouse) that happens to land on a slider silently rewrites the
 * setting. WebKit only jumps on a completed tap, which is just as unwanted.
 * The rule enforced here: only dragging the thumb changes the value;
 * track presses do nothing, and on touch the page still scrolls normally.
 *
 * For touch/pen the press cannot be suppressed without also blocking
 * scroll (preventDefault on touchstart does both), so the value is
 * snapshotted at pointerdown and restored for the rest of the gesture
 * whenever the press landed outside the thumb; scroll takeovers arrive as
 * pointercancel and get the same restore. Mouse has no scroll conflict,
 * so mousedown on the track is preventDefault'ed outright - Blink never
 * jumps the thumb under the pointer (which would also flash the thumb's
 * pointer cursor, as the pointer then hovers the jumped thumb).
 * Either way: track-click is off on every platform, thumb-drag only.
 */
function guardRangeTrackPress(control: HTMLInputElement) {
    // the rendered thumb is 1.5rem square; keep in sync with style.css
    const THUMB_WIDTH = 24;
    // a few px of slop so near-thumb presses behave like thumb grabs
    const HIT_SLOP = 4;

    let snapshot: number | null = null;
    let offThumb = false;
    let restoring = false;

    const restore = () => {
        if (restoring || snapshot === null || control.valueAsNumber === snapshot) {
            return;
        }
        restoring = true;
        control.valueAsNumber = snapshot;
        // re-sync the <output> displays wired up via the inline oninput handlers
        control.dispatchEvent(new Event('input'));
        restoring = false;
    };

    const pressOffThumb = (clientX: number) => {
        const rect = control.getBoundingClientRect();
        const min = control.min === '' ? 0 : Number(control.min);
        const max = control.max === '' ? 100 : Number(control.max);
        // thumb centers travel half a thumb width in from the track ends
        const fraction = (control.valueAsNumber - min) / (max - min);
        const thumbX = rect.left + THUMB_WIDTH / 2 + fraction * (rect.width - THUMB_WIDTH);
        return Math.abs(clientX - thumbX) > THUMB_WIDTH / 2 + HIT_SLOP;
    };

    control.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') {
            return; // handled at mousedown below
        }
        offThumb = pressOffThumb(event.clientX);
        snapshot = control.valueAsNumber;
    });

    control.addEventListener('mousedown', (event) => {
        if (pressOffThumb(event.clientX)) {
            event.preventDefault();
        }
    });

    // Blink fires input as it moves the thumb to the press point; restoring
    // on each one keeps the thumb (and the readout) put for the gesture
    control.addEventListener('input', () => {
        if (offThumb) {
            restore();
        }
    });

    const end = () => {
        if (offThumb) {
            restore();
        }
        offThumb = false;
        snapshot = null;
    };
    control.addEventListener('pointerup', end);
    control.addEventListener('pointercancel', end);
}

/**
 * Bind a range input to a numeric value: initialise the control from get(),
 * and on change call set() (which persists and updates the live config).
 */
function bindNumericControl(id: string, get: () => number, set: (value: number) => void) {
    const control = document.getElementById(id) as HTMLInputElement;
    if (control) {
        control.addEventListener('change', (event) => {
            set((event.target as HTMLInputElement).valueAsNumber);
        });

        control.valueAsNumber = get();
        // sync the <output> displays wired up via the inline oninput handlers
        control.dispatchEvent(new Event('input'));

        guardRangeTrackPress(control);
    }
}

/**
 * Bind a checkbox control to a boolean value: initialise the control from
 * get(), and on click call set().
 */
function bindBooleanControl(id: string, get: () => boolean, set: (value: boolean) => void) {
    const control = document.getElementById(id) as HTMLInputElement;
    if (control) {
        control.addEventListener('click', (event) => {
            set((event.target as HTMLInputElement).checked);
        });

        control.checked = get();
    }
}

/**
 * Make a big bpm readout editable: click (or Enter/Space while focused) to
 * swap it for a number input; Enter/blur commits, Escape cancels.
 * Committing updates the slider and fires its change event, reusing the
 * bindNumericControl path to persist and update settings.
 */
function makeBpmValueEditable(outputId: string, inputId: string, sliderId: string) {
    const output = document.getElementById(outputId) as HTMLOutputElement;
    const input = document.getElementById(inputId) as HTMLInputElement;
    const slider = document.getElementById(sliderId) as HTMLInputElement;

    if (!output || !input || !slider) {
        return;
    }

    let cancelled = false;

    const startEditing = () => {
        input.value = output.value;
        output.hidden = true;
        input.hidden = false;
        input.focus();
        input.select();
    };

    const stopEditing = () => {
        input.hidden = true;
        output.hidden = false;
    };

    const commit = () => {
        if (!isNaN(input.valueAsNumber)) {
            const min = Number(slider.min);
            const max = Number(slider.max);
            const value = Math.min(Math.max(Math.round(input.valueAsNumber), min), max);
            output.value = '' + value;
            slider.valueAsNumber = value;
            slider.dispatchEvent(new Event('change'));
        }
        stopEditing();
    };

    output.addEventListener('click', startEditing);
    output.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            startEditing();
        }
    });

    input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
            commit();
        } else if (event.key === 'Escape') {
            cancelled = true;
            stopEditing();
        }
    });

    input.addEventListener('blur', () => {
        // Escape hides the input, which fires blur; don't commit that
        if (cancelled) {
            cancelled = false;
            return;
        }
        commit();
    });
}

export {wireHintPopovers, bindNumericControl, bindBooleanControl, makeBpmValueEditable};
